<template>
    <div>
        <div v-if="sortedItems.length === 0">{{ toSentenceCase($t(emptyText)) }}</div>
        <ul v-else class="simple-list">
            <li
                v-for="(item, visibleIndex) in visibleItems"
                :key="'item' + visibleIndex"
                class="simple-list__item"
            >
                <span v-for="(field, fieldIndex) in displayFields" :key="`field-${fieldIndex}`">
                    {{ get(item, field) || $t(`general.defaults.${nullValueKey}`) }}
                </span>
            </li>
            <li v-if="showTooltip && hiddenItems.length" class="simple-list__more-items">
                <v-tooltip z-index="300" location="bottom">
                    <template v-slot:activator="{ props: activatorProps }">
                        <span v-bind="activatorProps">{{
                            $t(additionalItemsText, hiddenItems.length)
                        }}</span>
                    </template>

                    <ul class="simple-list">
                        <li
                            v-for="(item, hiddenIndex) in hiddenItems"
                            :key="'h-item' + hiddenIndex"
                            class="simple-list__item"
                        >
                            <span
                                v-for="(field, hiddenChildIndex) in displayFields"
                                :key="'h-field' + hiddenChildIndex"
                            >
                                {{ get(item, field) }}
                            </span>
                        </li>
                    </ul>
                </v-tooltip>
            </li>
        </ul>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { get, sortBy, lowerCase, uniqBy } from 'lodash';

const props = defineProps({
    items: {
        type: Array,
        default: () => [],
    },
    displayFields: Array,
    sort: {
        type: Boolean,
        required: false,
        default: false,
    },
    limit: Number,
    nullValueKey: String,
    additionalItemsText: {
        type: String,
        default: 'general.components.additionalItemsText',
    },
    showTooltip: {
        type: Boolean,
        default: true,
    },
    uniqByKey: String,
    emptyText: {
        type: String,
        default: '',
    },
});

const uniqueItems = computed(() =>
    props.uniqByKey ? uniqBy(props.items, props.uniqByKey) : props.items
);

const sortedItems = computed(() =>
    props.sort
        ? sortBy(uniqueItems.value, [
              i => (props.displayFields ? lowerCase(i.description) : props.displayFields),
          ])
        : uniqueItems.value
);

const visibleItems = computed(() =>
    sortedItems.value.slice(0, props.limit || sortedItems.value.length)
);

const hiddenItems = computed(() =>
    sortedItems.value.slice(props.limit || sortedItems.value.length)
);
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.simple-list {
    list-style-type: none;
    padding: 0;

    &__item {
        span {
            &:not(:last-child):after {
                content: '\2014'; // em dash
                margin: 0 0.3rem;
            }
        }
    }

    &__more-items {
        color: $promo-primary-colour;
    }
}
</style>
